import * as z from 'zod';

const emailField = z.string().email('Please enter a valid email address');

/**
 * The account password policy, in one place.
 *
 * It used to be written twice as zod (register and set-new-password) and twice
 * again as prose in the two forms that render the hint, with a comment asking
 * the next person to keep all four in sync by hand. `PASSWORD_HINT` is exported
 * alongside so changing the rule cannot leave the UI describing the old one.
 *
 * Matches Supabase's `password_min_length=8`.
 */
const passwordField = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    'Password must contain at least one uppercase letter, one lowercase letter, and one number'
  );

/** The rule as a sentence, for the hint under a password field. */
export const PASSWORD_HINT =
  'At least 8 characters, with a number and an uppercase letter.';

// Sign-in deliberately does NOT apply `passwordField`. A login form is not where
// the creation policy belongs: the only thing it can accomplish is locking out
// an account that predates the current setting, and it states the policy to
// anyone who opens the page. Supabase is the authority on whether the password
// is right; this just needs a non-empty value to send.
export const loginSchema = z.object({
  email: emailField,
  password: z.string().min(1, 'Enter your password'),
});

// No `confirmPassword`. It exists to catch typos the user cannot see, and the
// password field now has a reveal toggle, which does that job without making
// anyone type the same string twice. Full name stays: it feeds the Navbar
// display name and onboarding does not ask for it.
export const registerSchema = z.object({
  fullName: z
    .string()
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name must be less than 100 characters'),
  email: emailField,
  password: passwordField,
});

export const resetPasswordSchema = z.object({
  email: emailField,
});

// Set-a-new-password form shown after clicking the recovery link. Single field,
// for the same reason register dropped its confirmation - and especially here,
// where the user arrived precisely because they could not remember a password.
export const newPasswordSchema = z.object({
  password: passwordField,
});

export type LoginForm = z.infer<typeof loginSchema>;
export type RegisterForm = z.infer<typeof registerSchema>;
export type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;
export type NewPasswordForm = z.infer<typeof newPasswordSchema>;

export type AuthMode = 'login' | 'register' | 'reset';
export type AuthState =
  | 'form'
  | 'magic-link-sent'
  | 'registration-success'
  // Reached from SIGN-IN, not sign-up: the account exists but the confirmation
  // link was never clicked. Distinct from `registration-success` because no
  // mail was just sent - the link went out whenever they signed up.
  | 'email-unconfirmed'
  | 'reset-email-sent';
